/**
 * Versioned Chart Creator template fingerprints.
 *
 * FactSet does not document the worksheet layout of the workbooks returned by
 * `/download-excel-chart`. Everything we know is empirical, so this registry
 * only ever contains layouts that a real probe run
 * (`diagnostics/chartCreatorTemplateProbe.ts`) confirmed against live bytes.
 *
 * The contract that keeps this safe is deliberately narrow:
 *
 *   - A fingerprint is **matched**, never guessed. `detect` must key off
 *     structure the probe actually observed (sheet names, workbook names).
 *   - A workbook that matches no fingerprint falls through to generic
 *     name/label discovery, and from there to a full regenerate (Tier 2).
 *     A wrong-cell write is strictly worse than a regenerate.
 *   - `version` is part of the identity. When FactSet ships a new template
 *     shape, add a new entry rather than editing an existing one, so an older
 *     workbook that is still open keeps resolving to the layout it was built
 *     with.
 */

/** A single worksheet cell that holds one Chart Creator parameter. */
export interface ChartCreatorTemplateInput {
  sheet: string;
  /** A1 notation, single cell, no `$` and no sheet qualifier. */
  address: string;
}

/** What `detect` gets to look at. Structure only — never cell values. */
export interface ChartCreatorTemplateDetectContext {
  sheetNames: readonly string[];
  namedObjectNames: readonly string[];
}

export interface ChartCreatorTemplateFingerprint {
  /** Stable identity, e.g. `"chart-creator/2026-08"`. */
  version: string;
  /** Chart type codes this layout was probed against, for the audit trail. */
  probedChartTypes: readonly string[];
  detect: (context: ChartCreatorTemplateDetectContext) => boolean;
  /**
   * Parameter path (`chartType`, `tickers`, `summaryTable.period1`, …) to the
   * cell that holds it. Paths match the flattened keys used by
   * `executorNativeWorkbookChartTools`.
   */
  inputs: Readonly<Record<string, ChartCreatorTemplateInput>>;
  /**
   * Cell the summary-table layout combo box writes into, when the probe found
   * one. `ModifyShapes` can only move and resize shapes, so a combo box with
   * no linked cell cannot be driven from an intent — those layout changes have
   * to regenerate the workbook instead.
   */
  layoutLinkedCell?: ChartCreatorTemplateInput;
}

/**
 * Empty until a probe run confirms a layout. This is the intended initial
 * state, not a stub: with no fingerprints every workbook takes the generic
 * discovery path, which is exactly the conservative fallback we want.
 */
export const CHART_CREATOR_TEMPLATE_FINGERPRINTS: readonly ChartCreatorTemplateFingerprint[] =
  [];

export function matchChartCreatorTemplate(
  context: ChartCreatorTemplateDetectContext,
  fingerprints: readonly ChartCreatorTemplateFingerprint[] = CHART_CREATOR_TEMPLATE_FINGERPRINTS,
): ChartCreatorTemplateFingerprint | undefined {
  for (const fingerprint of fingerprints) {
    try {
      if (fingerprint.detect(context)) return fingerprint;
    } catch {
      // A throwing detector is a bug in that entry, not a reason to fail the
      // whole match. Skip it and let the remaining fingerprints run.
    }
  }
  return undefined;
}
